import React, { useEffect, Suspense, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from './store/authStore';
import { useThemeStore } from './store/themeStore';
import { Loading } from './components/ui/Loading';
import { ToastProvider } from './components/ui/Toast';
import { ErrorBoundary } from './components/ui/ErrorBoundary';
import { lazyWithRetry } from './utils/lazyWithRetry';
import { WaiverModal } from './components/auth/WaiverModal';
import { FeedbackModal } from './components/layout/FeedbackModal';
import { PaymentModal } from './components/ui/PaymentModal';
import { STRIPE_PLANS } from './config';

// Lazy load pages for performance optimization with retry logic
const Login = lazyWithRetry(() => import('./pages/Login').then(module => ({ default: module.Login })));
const SignUp = lazyWithRetry(() => import('./pages/SignUp').then(module => ({ default: module.SignUp })));
const Register = lazyWithRetry(() => import('./pages/Register').then(module => ({ default: module.Register })));
const ForgotPassword = lazyWithRetry(() => import('./pages/ForgotPassword').then(module => ({ default: module.ForgotPassword })));
const ResetPassword = lazyWithRetry(() => import('./pages/ResetPassword').then(module => ({ default: module.ResetPassword })));
const Pricing = lazyWithRetry(() => import('./pages/Pricing').then(module => ({ default: module.Pricing })));
const Dashboard = lazyWithRetry(() => import('./pages/Dashboard').then(module => ({ default: module.Dashboard })));
const CategoryFeed = lazyWithRetry(() => import('./pages/CategoryFeed').then(module => ({ default: module.CategoryFeed })));
const CustomerDetail = lazyWithRetry(() => import('./pages/CustomerDetail').then(module => ({ default: module.CustomerDetail })));
const Analytics = lazyWithRetry(() => import('./pages/Analytics').then(module => ({ default: module.Analytics })));
const Profile = lazyWithRetry(() => import('./pages/Profile').then(module => ({ default: module.Profile })));
const Settings = lazyWithRetry(() => import('./pages/Settings').then(module => ({ default: module.Settings })));
const IncidentReport = lazyWithRetry(() => import('./pages/IncidentReport').then(module => ({ default: module.IncidentReport })));
const Waiver = lazyWithRetry(() => import('./pages/Waiver').then(module => ({ default: module.Waiver })));
const SelectPlan = lazyWithRetry(() => import('./pages/SelectPlan').then(module => ({ default: module.SelectPlan })));
const Plans = lazyWithRetry(() => import('./pages/Plans').then(module => ({ default: module.Plans })));
const SupportChat = lazyWithRetry(() => import('./pages/SupportChat').then(module => ({ default: module.SupportChat })));
const AboutPage = lazyWithRetry(() => import('./pages/AboutPage').then(module => ({ default: module.AboutPage })));
const BillingAccount = lazyWithRetry(() => import('./pages/BillingAccount').then(module => ({ default: module.BillingAccount })));

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Admin Bypass: Skip waiver check
  if (user?.role === 'admin') {
    return <>{children}</>;
  }

  // Enforce Waiver
  if (user && !user.waiver_accepted) {
    return <Navigate to="/waiver" replace />;
  }

  return <>{children}</>;
};

const PlanGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const user = useAuthStore((state) => state.user);
  const location = useLocation();

  // 1. Check Authentication First
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Admin Bypass: Skip plan check
  if (user.role === 'admin') {
    return <>{children}</>;
  }

  // 2. Check Plan Permissions
  // Allowed: free, free_trial, pro, business
  const plan = user.plan || 'free';
  const isAllowed = ['free', 'free_trial', 'pro', 'business'].includes(plan);

  if (!isAllowed) {
    // Redirect to Plans page with upgrade CTA
    return <Navigate to="/plans" replace />;
  }

  return <>{children}</>;
};

function App() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);
  const checkAuth = useAuthStore((state) => state.checkAuth);
  const theme = useThemeStore((state) => state.theme);

  useEffect(() => {
    // Only check auth if we don't have a user but might have a session
    // If we just logged out, we shouldn't auto-login
    // REMOVED auto-login logic based on stored token
    // We only rely on checkAuth which calls /api/me to verify session cookie
    checkAuth();
  }, []);

  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  return (
    <ToastProvider>
      <Router>
        <ErrorBoundary>
          <Suspense fallback={<Loading />}>
            <FeedbackModal />
            <Routes>
            <Route 
              path="/login" 
              element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />} 
            />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/pricing" element={<Pricing />} />
            
            {/* Waiver Route - Protected but allows access if not accepted */}
            <Route 
              path="/waiver" 
              element={
                isAuthenticated ? (
                  user?.role === 'admin' ? <Navigate to="/dashboard" replace /> : (
                    user?.waiver_accepted ? <Navigate to="/dashboard" replace /> : <Waiver />
                  )
                ) : (
                  <Navigate to="/login" replace />
                )
              } 
            />

            <Route 
              path="/select-plan" 
              element={
                isAuthenticated ? (
                  user?.role === 'admin' ? <Navigate to="/dashboard" replace /> : <SelectPlan />
                ) : <Navigate to="/login" replace />
              } 
            />

            <Route 
              path="/plans" 
              element={
                <ProtectedRoute>
                  {user?.role === 'admin' ? <Navigate to="/dashboard" replace /> : <Plans />}
                </ProtectedRoute>
              } 
            />

            <Route path="/about" element={<AboutPage />} />

            {/* Public Homepage - Landing Page */}
            <Route 
              path="/" 
              element={
                <Dashboard />
              } 
            />
            
            {/* Logged-in Dashboard */}
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <PlanGuard>
                    <Dashboard />
                  </PlanGuard>
                </ProtectedRoute>
              } 
            />
            
            {/* Public Category Feed */}
            <Route 
              path="/category/:category" 
              element={
                <CategoryFeed />
              } 
            />
            
            {/* Public Customer Detail */}
            <Route 
              path="/customer/:id" 
              element={
                <CustomerDetail />
              } 
            />

            {/* Protected & Gated Routes */}
            <Route 
              path="/analytics" 
              element={
                <ProtectedRoute>
                  <PlanGuard>
                    <Analytics />
                  </PlanGuard>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/profile" 
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/settings" 
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/billing" 
              element={
                <ProtectedRoute>
                  <BillingAccount />
                </ProtectedRoute>
              } 
            />
            
            {/* Gated Features */}
            <Route 
              path="/incident-report" 
              element={
                <ProtectedRoute>
                  <PlanGuard>
                    <IncidentReport />
                  </PlanGuard>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/report" 
              element={
                <ProtectedRoute>
                  <PlanGuard>
                    <IncidentReport />
                  </PlanGuard>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/karen-chat-room" 
              element={
                <ProtectedRoute>
                  <PlanGuard>
                    <SupportChat />
                  </PlanGuard>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/chat" 
              element={
                <ProtectedRoute>
                  <PlanGuard>
                    <SupportChat />
                  </PlanGuard>
                </ProtectedRoute>
              } 
            />
            
            <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </Router>
    </ToastProvider>
  );
}

export default App;
